/**
 * 이용권별 기본 기능 권한(유료/무료 구분 기능).
 * - DB: platform_plan_permissions.extra.features.{key} (boolean)
 *   supabase/migrations/20261026190000_plan_permissions_ai_quota.sql
 *   supabase/migrations/20261027230000_plan_permissions_table_items.sql (표 항목 키 추가)
 * - 값이 없거나 표를 못 읽으면 키별 기본값(PLAN_FEATURES.freeDefault / 유료 = 사용)을 쓴다.
 * - 관리자 화면·이용권 비교표는 이 키들을 「표 항목」으로 묶어 쓴다(src/constants/planTableItems.ts).
 */
export type PlanFeatureKey =
  | 'daily_note'
  | 'todo_note'
  | 'assignment_note'
  | 'assignment_advanced'
  | 'point_item_note'
  | 'budget_note'
  | 'class_record_memos'
  | 'weekly_learning_guide'
  | 'workspace_learning_content'
  | 'workspace_evaluation_plan'
  | 'class_record'
  | 'behavior_record'
  | 'presentation'
  | 'worksheet'
  | 'content_registry'
  | 'dashboard'
  | 'main_presentation'
  | 'dashboard_presentation'
  | 'class_alarm'
  | 'remote_control'
  | 'team_class';

export type PlanFeatureMap = Record<PlanFeatureKey, boolean>;

export type PlanFeatureDef = {
  key: PlanFeatureKey;
  label: string;
  hint: string;
  /** 값이 저장돼 있지 않을 때 FREE 이용권 기본값(유료 이용권은 항상 true). 생략 = false(기존 규칙) */
  freeDefault?: boolean;
};

export const PLAN_FEATURES: PlanFeatureDef[] = [
  { key: 'daily_note', label: '데일리 노트', hint: '사이드 메뉴 · 데일리 노트 페이지' },
  { key: 'todo_note', label: 'ToDo 노트', hint: '사이드 메뉴 · ToDo 노트 페이지' },
  { key: 'assignment_note', label: '과제 노트', hint: '과제·과제 포인트 노트 · 대시보드 프레젠 과제 카드' },
  { key: 'assignment_advanced', label: '프로젝트·루틴·챌린지 과제', hint: '과제 노트에서 프로젝트·루틴·챌린지 과제 만들기' },
  { key: 'point_item_note', label: '포인트 노트', hint: '포인트 항목 노트 · 대시보드 프레젠 포인트' },
  { key: 'budget_note', label: '예산 노트', hint: '사이드 메뉴 · 예산 노트 페이지' },
  { key: 'class_record_memos', label: '기록 모아보기', hint: '사이드 메뉴 · 수업기록 모아보기 페이지' },
  { key: 'weekly_learning_guide', label: '주간학습 안내', hint: '사이드 메뉴 · 주간학습 안내 페이지' },
  { key: 'workspace_learning_content', label: '기본 학습내용 등록', hint: '학급 공통 설정' },
  { key: 'workspace_evaluation_plan', label: '기본 평가계획 등록', hint: '학급 공통 설정' },
  { key: 'class_record', label: '수업기록 열', hint: '프로그램 진도표 · 연간 진도표의 수업기록' },
  { key: 'behavior_record', label: '행동특성', hint: '연간 진도표 행동특성 열' },
  { key: 'presentation', label: '프레젠테이션 열기', hint: '진도표의 슬라이드·프레젠테이션 열기·목록' },
  { key: 'worksheet', label: '활동지 열기', hint: '진도표의 활동지 편집·목록' },
  { key: 'content_registry', label: '자료 등록', hint: '슬라이드 페이지·레이아웃·활동지 템플릿 등록' },
  { key: 'dashboard', label: '대시보드', hint: '대시보드(홈) 화면', freeDefault: true },
  { key: 'main_presentation', label: '메인 프레젠테이션', hint: '메인 프레젠테이션 탭(Shift+O)', freeDefault: true },
  /** @deprecated 예전 「대시보드 프레젠테이션」 토글 — 어디서도 막지 않는 값. 저장 시 main_presentation 과 같은 값으로 맞춘다(PLAN_FEATURE_MIRRORS) */
  { key: 'dashboard_presentation', label: '대시보드 프레젠테이션(이전 값)', hint: '사용하지 않음 · main_presentation 으로 대체' },
  { key: 'class_alarm', label: '수업 알람', hint: '상단 종 · 수업 알람 설정·팝업', freeDefault: true },
  { key: 'remote_control', label: '다른 기기 리모트', hint: '온라인 리모트(서버에서도 차단)' },
  { key: 'team_class', label: '팀 학급', hint: '팀 만들기·가입' },
];

export const PLAN_FEATURE_KEYS: PlanFeatureKey[] = PLAN_FEATURES.map((f) => f.key);

const FREE_DEFAULTS: Partial<Record<PlanFeatureKey, boolean>> = Object.fromEntries(
  PLAN_FEATURES.filter((f) => f.freeDefault).map((f) => [f.key, true])
);

/** 저장값이 없을 때의 기본값(프리 = freeDefault, 그 외 = 사용) */
export function defaultPlanFeature(key: PlanFeatureKey, isFree: boolean): boolean {
  return isFree ? FREE_DEFAULTS[key] === true : true;
}

/** 기존 동작과 같은 기본값: 프리 = 잠금(새 키는 freeDefault), 그 외 = 모두 사용 */
export function defaultPlanFeatures(isFree: boolean): PlanFeatureMap {
  const out = {} as PlanFeatureMap;
  for (const k of PLAN_FEATURE_KEYS) out[k] = defaultPlanFeature(k, isFree);
  return out;
}

/**
 * AI 세부 기능 키(extra.features 에 함께 저장).
 * 관리자 저장(normalizePlanRowToTable)·마이그레이션 20261027230000 이 세 키를 항상 명시 값으로 채운다.
 * 값이 없을 때(마이그레이션·저장 전 행)만 서버·화면 모두 예전 ai_other 열을 따른다(관리자 화면에는 없음).
 * 대응: ai_curriculum_input = AI 자료 입력·AI 범교과 구성, ai_evaluation = AI 평가 구성, ai_record_feedback = AI 기록 피드백.
 */
export type PlanAiFeatureKey = 'ai_curriculum_input' | 'ai_evaluation' | 'ai_record_feedback';
export const PLAN_AI_FEATURE_KEYS: PlanAiFeatureKey[] = ['ai_curriculum_input', 'ai_evaluation', 'ai_record_feedback'];
export type PlanAiFeatureMap = Partial<Record<PlanAiFeatureKey, boolean>>;

function featuresObject(extra: unknown): Record<string, unknown> | null {
  if (!extra || typeof extra !== 'object') return null;
  const features = (extra as { features?: unknown }).features;
  if (!features || typeof features !== 'object' || Array.isArray(features)) return null;
  return features as Record<string, unknown>;
}

/** extra jsonb → 값이 boolean 인 키만 */
export function parsePlanFeatures(extra: unknown): Partial<PlanFeatureMap> {
  const out: Partial<PlanFeatureMap> = {};
  const f = featuresObject(extra);
  if (!f) return out;
  for (const k of PLAN_FEATURE_KEYS) {
    if (typeof f[k] === 'boolean') out[k] = f[k] as boolean;
  }
  return out;
}

/** extra jsonb → AI 세부 기능 중 값이 boolean 인 키만 */
export function parsePlanAiFeatures(extra: unknown): PlanAiFeatureMap {
  const out: PlanAiFeatureMap = {};
  const f = featuresObject(extra);
  if (!f) return out;
  for (const k of PLAN_AI_FEATURE_KEYS) {
    if (typeof f[k] === 'boolean') out[k] = f[k] as boolean;
  }
  return out;
}

/** 유료 경로 → 기능 키(딥링크·사이드메뉴 공통). 해당 없으면 null */
export function planFeatureForPath(pathname: string): PlanFeatureKey | null {
  const path = (pathname.split('?')[0] || '').replace(/\/$/, '') || '/';
  const under = (base: string) => path === base || path.startsWith(`${base}/`);
  if (under('/todo-note')) return 'todo_note';
  if (under('/daily-note')) return 'daily_note';
  if (under('/budget-note') || under('/admin/budget-note')) return 'budget_note';
  if (under('/class-record-memos')) return 'class_record_memos';
  if (under('/weekly-learning-guide')) return 'weekly_learning_guide';
  if (under('/assignment-note') || under('/assignment-points-note')) return 'assignment_note';
  if (under('/point-item-note')) return 'point_item_note';
  if (under('/workspace-learning-content')) return 'workspace_learning_content';
  if (under('/workspace-evaluation-plan')) return 'workspace_evaluation_plan';
  if (under('/dashboard-presentation')) return 'main_presentation';
  return null;
}
